import {
    UserGeneratedConfigService,
    ImsUserConfig,
} from 'app/config/user_generated_config.service';
import * as _ from 'lodash';
import { CachedEventRegistry } from 'app/shared/events/cached_event_registry';
import {
    ImsImageryLayer,
    ImsImageryLayersService,
    VALID_IMAGERY_TYPES,
} from '../map/services/ImsImageryLayersService';

const configUtilsFile = require('../config_utils');
export const buildUrl = configUtilsFile.configUtils.buildUrl;

const IMAGERY_LAYERS = 'imageryLayers';

// This is a serializeable imagery layer object that is designed to be able to be stored locally and sorted
// It does not contain an actual reference to the Cesium Imagerylayer so that it can be jsonified
interface SimpleImageryLayer {
    name: string;
    label: string;
    url: string;
    uuid?: string;
    isShown?: boolean;
}

/**
 * Generates a 'unique' string on an imagery layer if it doesn't have a UUID from an API save
 * @param imageryLayer
 * @private
 */
function generateBsUuid(imageryLayer) {
    let uuid = '';
    uuid = uuid + imageryLayer.name;
    return uuid;
}

function buildCustomURL(imageryLayers) {
    return imageryLayers.map((provider) => {
        return provider;
    });
}

function setShownSimpleImageryLayer(
    simpleImageryLayers: SimpleImageryLayer[],
    value: boolean,
    simpleImageryLayer: SimpleImageryLayer,
) {
    const index = _.findIndex(simpleImageryLayers, {
        uuid: simpleImageryLayer.uuid,
    });
    if (index === -1) {
        throw new Error('can not find simple imagery layer');
    }
    simpleImageryLayers[index].isShown = value;
    return simpleImageryLayers;
}

/**
 * Because we wipe all imagery layers each change, we need some way of keeping the shown state
 * between each change. Get the imagery providers that are set to shown, loop through all the new
 * imagery layers and then add the necessary flags. Return the updated array
 * @param previousLayersInLocStorage
 * @param simpleImageryLayers
 * @private
 */
function updateShownLayersWithShownInfo(previousLayersInLocStorage, simpleImageryLayers) {
    const newSimpleImageryLayers = [];
    simpleImageryLayers.map((layer) => {
        const simpleImageryLayer = _.find(previousLayersInLocStorage, {
            uuid: layer.uuid,
        });
        if (_.has(simpleImageryLayer, 'isShown')) {
            layer.isShown = simpleImageryLayer.isShown;
            newSimpleImageryLayers.push(layer);
        } else {
            newSimpleImageryLayers.push(layer);
        }
    });
    return newSimpleImageryLayers;
}

/**
 * This class is designed to deal with the base imagery layers defined in config.js and the new config service
 * Eg. ArcGIS, WMTS, WMS layers, etc
 * It handles loading them into the map layers service and storing them in/retrieving them from local storage
 * by representing them as 'SimpleImageryLayer' objects that are JSONifiable
 */
export class BaseImageryLayersService {
    private _$rootScope: any;
    private _localStorageService: any;
    private _onChangeEventRegistry: CachedEventRegistry;
    private _imsImageryLayersService: ImsImageryLayersService;
    private _userGeneratedConfigService: UserGeneratedConfigService;

    private _simpleImageryLayers: SimpleImageryLayer[];

    constructor(
        $rootScope,
        localStorageService,
        ImsImageryLayersService: ImsImageryLayersService,
        userGeneratedConfigService,
    ) {
        this._$rootScope = $rootScope;
        this._localStorageService = localStorageService;
        this._userGeneratedConfigService = userGeneratedConfigService;
        this._imsImageryLayersService = ImsImageryLayersService;

        // Get the image layers once initially
        this._simpleImageryLayers = [];

        this._onChangeEventRegistry = new CachedEventRegistry();

        this._userGeneratedConfigService.onConfigChange(async (config: ImsUserConfig) => {
            const baseImageryLayerObjList = config.imageryLayers.mapLayerProviders.map((p) =>
                p.toBaseImageryLayersServiceLayerObj(),
            );
            await this._imsImageryLayersService.setRemoveBingBaseLayer(config.removeBaseBingLayer); //base bing layer removed if that option is checked
            const previousState = this._fetchLocalStorageState(); //keep a list of all the providers that we're currently showing
            this._removeAllImagery();
            const orderedImageryLayers = this._keepLayerSortOrderBetweenChange(
                previousState,
                baseImageryLayerObjList,
            );
            this._loadAndSetLayers(orderedImageryLayers, previousState).catch((e) =>
                console.error(e),
            );
            this._onChangeEventRegistry.notifyListeners(this.shownSimpleImageryLayers);
        });

        this._userGeneratedConfigService.onConfigLoad(async (config: ImsUserConfig) => {
            const baseImageryLayerObjList = config.imageryLayers.mapLayerProviders.map((p) =>
                p.toBaseImageryLayersServiceLayerObj(),
            );
            await this._imsImageryLayersService.setRemoveBingBaseLayer(config.removeBaseBingLayer); //base bing layer removed if that option is checked
            await this._loadAndSetLayers(baseImageryLayerObjList, this._fetchLocalStorageState());
            await this._onChangeEventRegistry.notifyListeners(this.shownSimpleImageryLayers);
        });
    }

    /**
     * Returns a copy of the simple imagery layer array
     * @returns {SimpleImageryLayer[]}
     */
    get simpleImageryLayers(): SimpleImageryLayer[] {
        return this._simpleImageryLayers.slice();
    }

    /**
     * This getter returns all the simple imagery layers that have an isShown flag set to true OR
     * if the imagery layer is set to show by default and has never been toggled by the user
     */
    get shownSimpleImageryLayers() {
        const shownImageryLayers = [];
        this.simpleImageryLayers.map((layer: SimpleImageryLayer) => {
            if (_.has(layer, 'isShown') && layer.isShown) {
                shownImageryLayers.push(layer);
            } else if (
                this._isSimpleImageryLayerShownByDefault(layer) &&
                !_.has(layer, 'isShown')
                //show this layer if the user hasn't toggled it on or off
            ) {
                shownImageryLayers.push(layer);
            }
        });
        return shownImageryLayers;
    }

    /**
     * Toggles the isShown boolean on the simpleImageryLayer
     * @param simpleImageryLayer
     * @param value
     */
    toggleShownSimpleImageryLayer(simpleImageryLayer: SimpleImageryLayer, value: boolean): void {
        let simpleImageryLayers = _.cloneDeep(this._simpleImageryLayers);
        simpleImageryLayers = setShownSimpleImageryLayer(
            simpleImageryLayers,
            value,
            simpleImageryLayer,
        );
        this.setSimpleImageryLayers(simpleImageryLayers);
        this._onChangeEventRegistry.notifyListeners(this.shownSimpleImageryLayers);
    }

    /**
     * Sets the total list of available simpleImageryLayer objects and stores it in local storage
     * to preserve the order
     * @param {SimpleImageryLayer[]} simpleImageryLayers
     */
    setSimpleImageryLayers(simpleImageryLayers: SimpleImageryLayer[]) {
        if (_.isEqual(this._simpleImageryLayers, simpleImageryLayers)) {
            return;
        }

        this._simpleImageryLayers = simpleImageryLayers;
        // Saves the simple imagery layers to keep their order
        this._localStorageService.set(IMAGERY_LAYERS, simpleImageryLayers);
        this._recalculateHeightsInImageryService();
        this._onChangeEventRegistry.notifyListeners(this.shownSimpleImageryLayers);
    }

    addOnMapLayersChanged(callback) {
        return this._onChangeEventRegistry.addEventListener(callback);
    }

    _keepLayerSortOrderBetweenChange(previousLayers, newLayers) {
        const sortedLayers = [];
        // We have to use a separate array here, because if newLayers[0] is not
        // in previousLayers then it will push it to sortedLayers[0] which would then
        // be overwritten by whatever is at previousLayers[0]
        const additionalLayers = [];
        newLayers.map((layer) => {
            const index = _.findIndex(previousLayers, { uuid: layer.uuid }); //find the position the previous layer was in
            if (index !== -1) {
                sortedLayers[index] = layer;
            } else {
                additionalLayers.push(layer);
            }
        });
        return sortedLayers.concat(additionalLayers);
    }

    async _loadAndSetLayers(imageryProviders: any[], previousState = null) {
        const imageryLayers = await this._loadBaseLayersFromConfig(imageryProviders); //this sets the imagery layers
        let simpleImageryLayers = this._getSimpleImageryLayers(imageryLayers);
        simpleImageryLayers = updateShownLayersWithShownInfo(previousState, simpleImageryLayers);
        this.setSimpleImageryLayers(simpleImageryLayers); //set some simple imagery layers in local storage
    }

    /**
     * This function loads in a config then adds a new imagery layer to the imagery layers service
     * @param config
     * @private
     */
    async _loadBaseLayersFromConfig(imageryLayers) {
        const addedImageryLayers = [];
        if (!imageryLayers || imageryLayers.constructor !== Array) {
            console.error('Config imageryLayers definition must be an array');
            return;
        }
        imageryLayers = buildCustomURL(imageryLayers);
        // The properties of these imagery layers use the same as the relevant cesium imageryLayer options properties.
        // The exception is that these also support a 'proxyPrefixUrl' and 'showByDefault'
        // For example: https://cesiumjs.org/Cesium/Build/Documentation/ArcGisMapServerImageryProvider.html
        for (const imageryLayer of imageryLayers) {
            if (ImsImageryLayersService.isValidImageryLayerType(imageryLayer.type)) {
                try {
                    await this._imsImageryLayersService.addImsImageryLayer(imageryLayer);
                } catch (e) {
                    console.error(e);
                }
                addedImageryLayers.push(imageryLayer);
            } else {
                console.error(`Warning: imageryLayer '${imageryLayer.name}' has an invalid type. 
                Type must be one of: ${VALID_IMAGERY_TYPES.join(', ')}`);
            }
        }
        return addedImageryLayers;
    }

    /**
     * Remove all the imagery layers managed by this object
     */
    _removeAllImagery() {
        this._simpleImageryLayers.forEach((l) =>
            this._imsImageryLayersService.removeImsImageryLayerByUuid(l.uuid),
        );
        this._simpleImageryLayers = [];
        this._localStorageService.remove(IMAGERY_LAYERS);
        this._onChangeEventRegistry.notifyListeners(this.shownSimpleImageryLayers);
    }

    /**
     * Gets SimpleImageryLayer objects for a list of IMSImageryLayer objects,
     * and orders them according to saved localstorage order
     * @param {ImsImageryLayer[]} imageryLayers
     * @return {SimpleImageryLayer[]}
     * @private
     */
    _getSimpleImageryLayers(imageryLayers: ImsImageryLayer[]): SimpleImageryLayer[] {
        const simpleImageryLayers: SimpleImageryLayer[] = [];
        const localImageryLayers = this._localStorageService.get(IMAGERY_LAYERS);

        if (localImageryLayers && localImageryLayers.length > 0) {
            _.forEach(localImageryLayers, (localImageryLayer) => {
                _.forEach(imageryLayers, (imageryLayer) => {
                    if (localImageryLayer.uuid === imageryLayer.uuid) {
                        const simpleImageryLayer = {
                            name: imageryLayer.name,
                            label: imageryLayer.name,
                            url: imageryLayer.url,
                            uuid: imageryLayer.uuid,
                        };
                        simpleImageryLayers.push(simpleImageryLayer);
                    }
                });
            });
            if (imageryLayers.length != simpleImageryLayers.length) {
                const arrayOfUuids = [];
                _.forEach(simpleImageryLayers, (imageryLayer) => {
                    arrayOfUuids.push(imageryLayer.uuid);
                });
                _.forEach(imageryLayers, (imageryLayer) => {
                    if (imageryLayer.hideInMapLayers) return;

                    if (!_.includes(arrayOfUuids, imageryLayer.uuid)) {
                        const simpleImageryLayer = {
                            name: imageryLayer.name,
                            label: imageryLayer.name,
                            url: imageryLayer.url,
                            uuid: imageryLayer.uuid,
                        };
                        simpleImageryLayers.push(simpleImageryLayer);
                    }
                });
            }
        } else {
            for (let i = 0; i < imageryLayers.length; i++) {
                if (imageryLayers[i].hideInMapLayers) continue;
                const simpleImageryLayer = {
                    name: imageryLayers[i].name,
                    label: imageryLayers[i].name,
                    url: imageryLayers[i].url,
                    uuid: imageryLayers[i].uuid,
                };
                simpleImageryLayers.push(simpleImageryLayer);
            }
        }
        return simpleImageryLayers;
    }

    _fetchLocalStorageState() {
        return this._localStorageService.get(IMAGERY_LAYERS);
    }

    _isSimpleImageryLayerShownByDefault(layer: SimpleImageryLayer) {
        const imageryLayer = this._imsImageryLayersService.getLayerByUuid(layer.uuid);
        if (!imageryLayer) {
            throw new Error(`Can not find simple imagery layer for ${layer.name}`);
        }
        if (!_.has(layer, 'showByDefault')) {
            return imageryLayer.showByDefault;
        } else {
            return false;
        }
    }

    _recalculateHeightsInImageryService() {
        this._imsImageryLayersService.hideImageryLayersWithBaseHeightGroupIndex();
        const layers = this.shownSimpleImageryLayers;
        for (let i = layers.length - 1; i >= 0; i--) {
            const imsImageryLayer = this._imsImageryLayersService.getLayerByUuid(layers[i].uuid);
            this._imsImageryLayersService.raiseLayerToTopOfHeightGroup(imsImageryLayer);
            this._imsImageryLayersService.showLayer(imsImageryLayer);
        }
    }
}
